import React, { useEffect, useMemo, useState } from 'react';
import { HbrAccordion } from '@harbor/elements-react-standalone';
import { LearningSection } from './types';

interface Props {
  sections: LearningSection[];
  selectedItemId?: string;
  onSelectItem: (itemId: string) => void;
}

function itemTypeLabel(type: string) {
  switch (type) {
    case 'page':
      return 'Page';
    case 'lesson':
      return 'Lesson';
    case 'quiz':
      return 'Quiz';
    case 'resource':
      return 'Resource';
    case 'url':
      return 'Link';
    case 'forum':
      return 'Forum';
    default:
      return 'Item';
  }
}

export default function LearningSidebar({
                                          sections,
                                          selectedItemId,
                                          onSelectItem,
                                        }: Props) {
  const defaultOpenSectionIds = useMemo(() => {
    const selectedSection = sections.find((section) =>
      section.items.some((item) => item.id === selectedItemId)
    );

    if (selectedSection) {
      return [selectedSection.id];
    }

    return sections.length ? [sections[0].id] : [];
  }, [sections, selectedItemId]);

  const [openSectionIds, setOpenSectionIds] = useState<string[]>(
    defaultOpenSectionIds.map(String)
  );

  useEffect(() => {
    setOpenSectionIds(defaultOpenSectionIds.map(String));
  }, [defaultOpenSectionIds]);

  const handleAccordionShow = (sectionId: string) => {
    setOpenSectionIds([String(sectionId)]);
  };

  const handleAccordionHide = (sectionId: string) => {
    setOpenSectionIds((prev) => prev.filter((id) => id !== String(sectionId)));
  };

  return (
    <aside className="learning-sidebar">
      <div className="learning-sidebar-inner">
        {sections.map((section) => {
          const isOpen = openSectionIds.includes(String(section.id));

          return (
            <div key={section.id} className="learning-sidebar-section">
              <HbrAccordion
                open={isOpen}
                uncontrolled={false}
                variant="stacked"
                size="medium"
                label={section.title}
                triggerposition="right"
                onHbr-show={() => handleAccordionShow(String(section.id))}
                onHbr-hide={() => handleAccordionHide(String(section.id))}
              >
                <div className="learning-sidebar-items">
                  {section.items.map((item) => {
                    const selected = item.id === selectedItemId;
                    const complete = item.isComplete === true;

                    return (
                      <button
                        key={item.id}
                        type="button"
                        className={`learning-sidebar-item ${selected ? 'selected' : ''}`}
                        onClick={() => onSelectItem(item.id)}
                        aria-current={selected ? 'page' : undefined}
                      >
                        <div className="learning-sidebar-item-main">
                          <span
                            className={`learning-sidebar-status ${
                              complete ? 'complete' : 'incomplete'
                            }`}
                          >
                            {complete ? '●' : '○'}
                          </span>
                          <span className="learning-sidebar-item-title">{item.title}</span>
                        </div>
                        <div className="learning-sidebar-item-meta">
                          {itemTypeLabel(item.type)}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </HbrAccordion>
            </div>
          );
        })}
      </div>

      <style>{`
        .learning-sidebar {
          width: 320px;
          min-width: 320px;
          max-width: 320px;
          border-right: 1px solid #e5e7eb;
          background: #fff;
          overflow: auto;
        }

        .learning-sidebar-inner {
          padding: 1rem;
        }

        .learning-sidebar-section + .learning-sidebar-section {
          margin-top: 0.75rem;
        }

        .learning-sidebar-items {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          padding-top: 0.25rem;
        }

        .learning-sidebar-item {
          text-align: left;
          width: 100%;
          border: 1px solid rgba(0,0,0,0.08);
          background: #fff;
          border-radius: 10px;
          padding: 0.75rem 0.85rem;
          cursor: pointer;
          transition: background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
        }

        .learning-sidebar-item:hover {
          background: #f8fbff;
          border-color: rgba(10,102,255,0.25);
        }

        .learning-sidebar-item.selected {
          background: #eef5ff;
          border-color: #0a66ff;
          box-shadow: 0 0 0 1px rgba(10,102,255,0.08);
        } 

        .learning-sidebar-item-main {
          display: flex;
          align-items: flex-start;
          gap: 0.5rem;
        }

        .learning-sidebar-status {
          font-size: 0.9rem;
          line-height: 1.2;
          margin-top: 0.05rem;
        }

        .learning-sidebar-status.complete {
          color: #10b981;
        }

        .learning-sidebar-status.incomplete {
          color: #9ca3af;
        }

        .learning-sidebar-item-title {
          font-size: 0.92rem;
          font-weight: 600;
          color: #111827;
          line-height: 1.3;
        }

        .learning-sidebar-item-meta {
          margin-top: 0.3rem;
          margin-left: 1.35rem;
          font-size: 0.76rem;
          color: #6b7280;
        }
      `}</style>
    </aside>
  );
}